import { ReactComponent as DeliveryNoteIcon } from 'assets/dialogue-avatar-delivery-note.svg';
import { extLnkCollectMissedDelivery, extLnkPostOfficeLocator } from 'config/track-config';
import { CollectionPointData } from 'models/CollectionPoint';
import { DialogueCardData, DialogueType } from 'models/DialogCard';
import { ArticleEvent, Milestone } from 'models/shipments';
import { getEventDateTimeFromISO } from 'utils/date.utils';

import ArrowLink from './Arrows/ArrowLink';

const attemptedDeliveryDefaults = (eventReason?: string) => ({
  analyticsId: 'attemptedDelivery',
  type: DialogueType.DELIVERY_NOTE,
  icon: <DeliveryNoteIcon />,
  title: eventReason ?? 'We tried to deliver your parcel',
});

const dcWithParcelCollect = '95';

const unknownCollectionPointCard = (eventReason: string) => ({
  ...attemptedDeliveryDefaults(eventReason),
  content: (
    <>
      <p>We couldn’t deliver your parcel, so we’re taking it to a nearby collection point.</p>
      <p>We’ll let you know when and where to collect it.</p>
    </>
  ),
});

const collectionPointCard = (workCentre: string, eventReason: string, dateStr: string, toWcid: string) => ({
  ...attemptedDeliveryDefaults(eventReason),
  content: (
    <>
      <p>
        {`We couldn’t deliver your parcel, so we’re taking it to ${workCentre} for collection `}
        <strong>{`after ${dateStr}.`}</strong>
      </p>
      <p>Your parcel will not be ready before this time.</p>
      <p>Photo ID will be required.</p>
    </>
  ),
  actions: (
    <>
      <ArrowLink href={extLnkPostOfficeLocator + toWcid}>View location and collection hours</ArrowLink>
      <ArrowLink href={extLnkCollectMissedDelivery}>More collection options</ArrowLink>
    </>
  ),
});

export function getAttemptedDeliveryCard(
  latestEvent: ArticleEvent | null,
  workCentre: CollectionPointData | null
): DialogueCardData | null {
  const { milestone, toWcid, description: eventReason = '', readyToCollectDateTime } = latestEvent ?? {};
  if (milestone !== Milestone.ATTEMPTED_DELIVERY) return null;

  const { name: workCentreName, service_codes, type } = workCentre ?? {};
  const nonParcelCollectDC = type === 'DC' && !service_codes?.includes(dcWithParcelCollect);

  const collectionTime = getCollectionDateTime(readyToCollectDateTime);
  if (nonParcelCollectDC || !workCentreName || !toWcid || !collectionTime)
    return unknownCollectionPointCard(eventReason);
  return collectionPointCard(workCentreName, eventReason, collectionTime, toWcid);
}

function getCollectionDateTime(dateTimeStr: string | null | undefined) {
  if (!dateTimeStr) return null;
  const formattedDateTime = dateTimeStr.split(':').slice(0, 2).join(':');
  const { date, time } = getEventDateTimeFromISO(formattedDateTime) || {};
  if (!date || !time) return null;
  return `${time}, ${date}`;
}
